import store from '@/store';
import cacheManager from './cache-manager';
import log from '@/configured/logger';
import user from '@/components/user';
// import * as OfflinePluginRuntime from 'offline-plugin/runtime';

const serviceWorkerUsed = navigator && navigator.serviceWorker;
const applicationCacheUsed = !serviceWorkerUsed && window.applicationCache;
const exports = {
  namespace: 'offlineDownload',
  module: {
    namespaced: true,
    state: {
      offlineModeEnabled: false,
      downloadInitialized: false,
      downloadLoading: false,
      downloadReady: false,
      downloadUpdating: false,
      downloadError: false,
    },
    mutations: {
      SET_OFFLINE_ENABLED: (state, status) =>
        (state.offlineModeEnabled = status),
      DOWNLOAD_INITIALIZED,
      DOWNLOAD_COMPLETE,
      DOWNLOAD_REMOVED,
      ON_UPDATING,
      CLEAR,
    },
    actions: {
      setOfflineModeEnabled,
      initCache,
      removeCache,
      resetDownloadStatus({ commit }) {
        commit('DOWNLOAD_REMOVED');
      },
      clear({ commit }) {
        commit('CLEAR');
      },
    },
    getters: {},
  },
};

store.registerModule(exports.namespace.split('/'), exports.module);

store.subscribe((mutation) => {
  if (mutation.type === 'RESTORE_MUTATION') {
    store.dispatch(`${exports.namespace}/resetDownloadStatus`);
    if (applicationCacheUsed) {
      log.info('App cache enabled at page load, initializing cache.');
      store.dispatch(`${exports.namespace}/initCache`, true);
    } else if (store.state[exports.namespace].offlineModeEnabled) {
      log.info('Offline mode enabled at page load, initializing cache.');
      store.dispatch(`${exports.namespace}/initCache`, true);
    }
  }
});

export default exports;

//////////

function initCache({ commit }) {
  commit('DOWNLOAD_INITIALIZED');

  return cacheManager.init({
    onInstalledCallback() {
      if (isStillEnabledOnCallback() || applicationCacheUsed) {
        commit('DOWNLOAD_COMPLETE');
      }
    },
    onUpdatingCallback() {
      if (isStillEnabledOnCallback() || applicationCacheUsed) {
        commit('ON_UPDATING');
      }
    },
    onUpdateReadyCallback() {
      if (isStillEnabledOnCallback() || applicationCacheUsed) {
        commit('ON_UPDATING');
        // Disabled for now
        // jkca: 2023-04-10
        // OfflinePluginRuntime.applyUpdate();
      }
    },
    onUpdatedCallback() {
      if (isStillEnabledOnCallback() || applicationCacheUsed) {
        commit('ON_UPDATING');
        window.location.reload();
      }
    },
    onUpdateFailedCallback() {
      if (isStillEnabledOnCallback() || applicationCacheUsed) {
        commit('ON_FAILED');
      }
    },
  });

  function isStillEnabledOnCallback() {
    const enabled = store.state[exports.namespace].offlineModeEnabled;
    log.info('isStillEnabledOnCallback offlineModeEnabled', enabled);
    return !!enabled;
  }
}

function removeCache({ commit, state }, keepState) {
  return cacheManager.remove().then(() => {
    if (keepState) {
      commit('CLEAR');
    }
    if (serviceWorkerUsed) {
      window.location.reload();
    }
  });
}

function DOWNLOAD_INITIALIZED(state) {
  state.downloadInitialized = true;
  state.downloadLoading = true;
  state.downloadReady = false;
  state.downloadUpdating = false;
  state.downloadError = false;
}

function DOWNLOAD_COMPLETE(state) {
  state.downloadInitialized = true;
  state.downloadLoading = false;
  state.downloadReady = true;
  state.downloadUpdating = false;
  state.downloadError = false;
}

function DOWNLOAD_REMOVED(state) {
  state.downloadInitialized = false;
  state.downloadLoading = false;
  state.downloadReady = false;
  state.downloadUpdating = false;
  state.downloadError = false;
}

function CLEAR(state) {
  state.downloadInitialized = false;
  state.downloadLoading = false;
  state.downloadReady = false;
  state.downloadUpdating = false;
  state.downloadError = false;
  state.offlineModeEnabled = false;
}

function ON_UPDATING(state) {
  if (!state.downloadUpdating) {
    state.downloadInitialized = true;
    state.downloadLoading = true;
    state.downloadUpdating = true;
  }
}

function setOfflineModeEnabled({ commit, state }, { enabled, keepState }) {
  log.info('setOfflineModeEnabled', enabled, keepState);
  if (enabled === state.offlineModeEnabled) {
    log.info('setOfflineModeEnabled did not change, skipping');
    return;
  }

  const userSavePromise = store.dispatch(
    `${user.store.namespace}/updateUserData`,
    {
      'fields[offlineMode]': enabled ? '1' : '',
    }
  );

  commit('SET_OFFLINE_ENABLED', enabled);
  commit('ON_UPDATING');

  if (enabled && (serviceWorkerUsed || applicationCacheUsed)) {
    if (serviceWorkerUsed) {
      log.info('Service worker detected for offline enabling');
    } else if (applicationCacheUsed) {
      log.info('applicationCache detected for offline enabling');
    }

    userSavePromise.then(
      () => {
        store.dispatch(`${exports.namespace}/initCache`);
      },
      () => {
        log.warn('Failed to save user.');
      }
    );
  } else if (!enabled && (serviceWorkerUsed || applicationCacheUsed)) {
    if (serviceWorkerUsed) {
      log.info('Service worker detected for offline disabling');
    } else if (applicationCacheUsed) {
      log.info('applicationCache detected for offline disabling');
    }

    userSavePromise.then(() => {
      store.dispatch(`${exports.namespace}/removeCache`, keepState);
    });
  } else {
    commit('DOWNLOAD_COMPLETE');
  }
}
